import { useState } from "react";
import { Plus, TrendingUp, TrendingDown, DollarSign, Pencil, Trash2 } from "lucide-react";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import {
  Modal, Form, Input, InputNumber, DatePicker, Select,
  Button, Tag, Popconfirm, Table, message, Checkbox, Tooltip,
} from "antd";
import dayjs from "dayjs";
import { useLedgerList, useLedgerCreate, useLedgerUpdate, useLedgerDelete, ILedgerEntry } from "../../../hooks/api/ledger/hooks";
import { useClientList } from "../../../hooks/api/clients/hooks";
import { IClient } from "../../../types/api/client";

function fmt(n: number) {
  return `£${Number(n ?? 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CATEGORY_OPTS = [
  { value: "invoice_payment", label: "Invoice Payment" },
  { value: "purchase", label: "Purchase" },
  { value: "expense", label: "Expense" },
  { value: "refund", label: "Refund" },
  { value: "adjustment", label: "Adjustment" },
  { value: "other", label: "Other" },
];

function EntryModal({ open, editing, onClose, onSuccess }: {
  open: boolean; editing?: ILedgerEntry | null; onClose: () => void; onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();
  const isEdit = !!editing;

  const { data: clientRes } = useClientList({ page: 1, limit: 200 });
  const clients: IClient[] = (clientRes?.data as IClient[] | undefined) ?? [];
  const clientOptions = clients.map((c) => ({ value: c.id, label: c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() }));

  const { mutate: create, isPending: creating } = useLedgerCreate({
    onSuccess: () => { messageApi.success("Entry created."); form.resetFields(); onSuccess(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });
  const { mutate: update, isPending: updating } = useLedgerUpdate({
    onSuccess: () => { messageApi.success("Entry updated."); onSuccess(); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const handleOpen = (v: boolean) => {
    if (!v) return;
    if (editing) {
      form.setFieldsValue({
        entry_type: editing.entry_type,
        category: editing.category,
        amount: editing.amount,
        currency: editing.currency,
        reference: editing.reference,
        description: editing.description,
        entry_date: editing.entry_date ? dayjs(editing.entry_date) : null,
        client_id: editing.client_id,
        is_reconciled: editing.is_reconciled,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ entry_type: "credit", category: "other", currency: "GBP", entry_date: dayjs() });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const payload = { ...values, entry_date: values.entry_date ? values.entry_date.format("YYYY-MM-DD") : null };
      if (isEdit && editing) { update({ ...payload, id: editing.id }); } else { create(payload); }
    });
  };

  return (
    <Modal open={open} onCancel={onClose} afterOpenChange={handleOpen} title={isEdit ? "Edit Entry" : "New Ledger Entry"} width={520} footer={null} destroyOnClose>
      {ctx}
      <Form form={form} layout="vertical" size="small" className="mt-2">
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="entry_type" label="Type" rules={[{ required: true }]}>
            <Select options={[{ value: "credit", label: "Credit (Income)" }, { value: "debit", label: "Debit (Expense)" }]} />
          </Form.Item>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select options={CATEGORY_OPTS} />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="amount" label="Amount (£)" rules={[{ required: true }]}>
            <InputNumber min={0.01} step={0.01} prefix="£" className="w-full" />
          </Form.Item>
          <Form.Item name="entry_date" label="Date" rules={[{ required: true }]}>
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <Form.Item name="client_id" label="Client (optional)">
          <Select showSearch allowClear placeholder="Select client…" options={clientOptions}
            filterOption={(input, opt) => ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())} />
        </Form.Item>
        <Form.Item name="reference" label="Reference">
          <Input placeholder="e.g. Invoice #, PO #, Bank ref" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="is_reconciled" valuePropName="checked">
          <Checkbox>Mark as reconciled</Checkbox>
        </Form.Item>
        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={creating || updating} onClick={handleSubmit}>
            {isEdit ? "Save" : "Add Entry"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

const LedgerPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ILedgerEntry | null>(null);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [messageApi, ctx] = message.useMessage();

  const queryParams = { page, limit: 20, ...(typeFilter ? { entry_type: typeFilter } : {}) };
  const { data: listData, isLoading, refetchWithQuery } = useLedgerList(queryParams);
  const entries: ILedgerEntry[] = listData?.data ?? [];
  const extra = (listData as any)?.extra ?? {};
  const total = Number(extra.total ?? 0);
  const totalCredits = Number(extra.total_credits ?? 0);
  const totalDebits = Number(extra.total_debits ?? 0);
  const balance = Number(extra.balance ?? 0);

  const { mutate: deleteEntry } = useLedgerDelete({
    onSuccess: () => { messageApi.success("Entry deleted."); refetchWithQuery({ page }); },
    onError: (e: any) => messageApi.error(e?.response?.data?.message || e?.message || "Failed."),
  });

  const columns = [
    { title: "Date", dataIndex: "entry_date", key: "entry_date", render: (v: string) => <span className="text-xs text-slate-500 whitespace-nowrap">{v ? dayjs(v).format("DD MMM YYYY") : "—"}</span> },
    { title: "Type", dataIndex: "entry_type", key: "entry_type", render: (v: string) => <Tag color={v === "credit" ? "green" : "red"}>{v === "credit" ? "Credit" : "Debit"}</Tag> },
    { title: "Category", dataIndex: "category", key: "category", render: (v: string) => <span className="text-xs text-slate-500">{v.replace(/_/g, " ")}</span> },
    { title: "Client", dataIndex: "client_name", key: "client_name", render: (v: string | null) => <span className="text-sm text-slate-700">{v || "—"}</span> },
    { title: "Reference", dataIndex: "reference", key: "reference", render: (v: string | null) => <span className="text-xs text-slate-500 font-mono">{v || "—"}</span> },
    { title: "Description", dataIndex: "description", key: "description", render: (v: string | null) => <span className="text-xs text-slate-500 truncate max-w-[200px] block">{v || "—"}</span> },
    { title: "Amount", dataIndex: "amount", key: "amount", align: "right" as const, render: (v: number, row: ILedgerEntry) => <span className={`font-bold tabular-nums ${row.entry_type === "credit" ? "text-emerald-600" : "text-red-500"}`}>{row.entry_type === "credit" ? "+" : "-"}{fmt(v)}</span> },
    { title: "Rec.", dataIndex: "is_reconciled", key: "is_reconciled", render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_: any, row: ILedgerEntry) => (
        <div className="flex items-center gap-1">
          <Tooltip title="Edit"><Button size="small" icon={<Pencil size={11} />} onClick={() => { setEditing(row); setModalOpen(true); }} /></Tooltip>
          <Popconfirm title="Delete this entry?" onConfirm={() => deleteEntry(row.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Tooltip title="Delete"><Button size="small" icon={<Trash2 size={11} />} danger type="text" /></Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Ledger</h1>
          <p className="text-slate-400 text-sm">Financial credits, debits and account reconciliation</p>
        </div>
        <ButtonWrapper type="primary" icon={<Plus size={15} />} onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-1.5">
          New Entry
        </ButtonWrapper>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><TrendingUp size={18} className="text-emerald-500" /></div>
          <div><div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">Total Credits</div><div className="text-2xl font-bold text-emerald-600">{fmt(totalCredits)}</div></div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center"><TrendingDown size={18} className="text-red-500" /></div>
          <div><div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">Total Debits</div><div className="text-2xl font-bold text-red-500">{fmt(totalDebits)}</div></div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${balance >= 0 ? "bg-blue-50" : "bg-amber-50"}`}><DollarSign size={18} className={balance >= 0 ? "text-blue-500" : "text-amber-600"} /></div>
          <div><div className="text-[11px] text-slate-400 uppercase tracking-wide font-medium">Balance</div><div className={`text-2xl font-bold ${balance >= 0 ? "text-blue-600" : "text-amber-600"}`}>{balance < 0 ? `-${fmt(Math.abs(balance))}` : fmt(balance)}</div></div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 mb-4">
        <div className="flex items-center gap-3">
          <Select allowClear placeholder="Filter by type" style={{ width: 180 }} value={typeFilter}
            onChange={(v) => { setTypeFilter(v); setPage(1); refetchWithQuery({ page: 1, entry_type: v }); }}
            options={[{ value: "credit", label: "Credits only" }, { value: "debit", label: "Debits only" }]} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={entries}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, showTotal: (t) => `${t} entries`, onChange: (p) => { setPage(p); refetchWithQuery({ page: p }); } }}
          scroll={{ x: 900 }}
        />
      </div>

      <EntryModal open={modalOpen} editing={editing} onClose={() => { setModalOpen(false); setEditing(null); }} onSuccess={() => { setModalOpen(false); setEditing(null); refetchWithQuery({ page }); }} />
    </div>
  );
};

export default LedgerPage;
